import { Component, Inject, Input, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AgGridLangMap,
  DeleteDependencies,
  DocTypes,
  ErrorDependenciesItem,
  IoniconsFilled,
  IoniconsOutline,
  PtEntity,
  PtRepositoryEntity,
  PtUiAppRoutes
} from '@pt/pt-models';
// noinspection ES6PreferShortImport
import { ErrorDependencyService } from '../services/error-dependency.service';
import { ColDef, GridOptions, ITooltipComp, ITooltipParams } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { CustomRendererComponent } from '../ag-grid';
import { Store } from '@ngrx/store';
import { PtCoreActions } from '@pt/pt-core';
import { WidgetConfig } from '../../../../pt-core/src/lib/widget-config';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';

@Component({
  selector: 'pt-error-dependency',
  template: `
    <div class="modal-header" (click)="$event.stopPropagation()">
      <h4 class="modal-title">
        {{ 'TITLE.DELETE_FAILED' | translate }}
      </h4>
    </div>

    <div class="modal-body" (click)="$event.stopPropagation()">
      <h5 class="d-flex align-items-center">
        <pt-icon class="text-warning" [size]="32" [key]="IconWarning"></pt-icon>
        {{ currentEntity?.name ? currentEntity.name : dependencyEntity }}
      </h5>
      <label>{{ 'MESSAGE.DEPENDENCIES_DELETE_ERROR' | translate }}</label>
      <ag-grid-angular
        *ngIf="rowData?.length > 0"
        style="width: 100%; height: 325px;"
        class="ag-theme-balham"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [gridOptions]="gridOptions"
      >
      </ag-grid-angular>
    </div>

    <div class="modal-footer" (click)="$event.stopPropagation()">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        (click)="activeModal.close(false); $event.stopPropagation()"
      >
        {{ 'BUTTON.CLOSE' | translate }}
      </button>
    </div>

    <ng-template #actionCol let-node>
      <pt-icon
        class="cursor-pointer"
        [key]="IconOpen"
        (click)="selectItem(node.row)"
      ></pt-icon>
    </ng-template>

    <ng-template #nameCol let-node>
      <div
        [ngbTooltip]="node.tooltip"
        placement="right"
        tooltipClass="input-password-help-tooltip"
      >
        <pt-image-renderer
          [label]="node.row.name"
          [type]="'ENTITY'"
          [entity]="iconPtEntity"
        >
          <div additionalContentRight>
            <span
              class="badge rounded-pill bg-success ms-2"
              *ngIf="node.row.validated"
              >{{ 'TOOLTIP.VALIDATED' | translate }}</span
            >
          </div>
        </pt-image-renderer>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .custom-tooltip {
        width: 100px;
        min-height: 30px;
        border: 1px solid #858585;
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-content: center;
      }

      .custom-tooltip p {
        margin: 5px;
        text-align: center;
        width: 100%;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ErrorDependencyDialogComponent implements OnInit {
  @ViewChild('actionCol', { static: true }) actionCol: TemplateRef<unknown>;
  @ViewChild('nameCol', { static: true }) nameCol: TemplateRef<unknown>;

  @Input() error: DeleteDependencies;

  public IconWarning = IoniconsFilled.warning;
  public IconOpen = IoniconsOutline.open;

  public currentEntity: any = null;

  public rowData = [];
  public columnDefs: ColDef[] = [];
  public gridOptions: GridOptions;
  public dependencyEntity: PtRepositoryEntity;
  public iconPtEntity: PtEntity;
  public i18NTooltip: string;
  public entityTooltip?: string;
  private agGridLangMap: AgGridLangMap;
  private readonly env;

  constructor(
    @Inject(WidgetConfig) private config,
    private store: Store,
    public activeModal: NgbActiveModal,
    private translateService: TranslateService,
    private errorDependencyService: ErrorDependencyService
  ) {
    this.agGridLangMap = new AgGridLangMap(this.translateService.currentLang);
    this.env = this.config.env;
  }

  ngOnInit(): void {
    this.columnDefs = this.createColumnDefs();
    this.gridOptions = this.createGridOptions();

    if (this.error && this.error?.dependencies?.entity) {
      const listDE = (this.error?.dependencies?.entity as string)?.split('.');
      this.dependencyEntity = listDE[listDE.length - 1] as PtRepositoryEntity;
      this.setIconPtEntityAndTooltip();
      if (this.i18NTooltip) {
        this.translateService.stream(this.i18NTooltip).subscribe(
          (tooltip) => {
            if (this.i18NTooltip === tooltip) {
              this.process(this.error, '');
            } else {
              this.process(this.error, tooltip);
            }
          },
          () => {
            this.process(this.error, '');
          }
        );
      } else {
        this.process(this.error, '');
      }
    }
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        field: 'name',
        headerName: this.agGridLangMap.getOf('name'),
        flex: 1,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        tooltipField: 'name',
        tooltipComponent: CustomTooltip,
        cellRenderer: CustomRendererComponent,
        cellRendererParams: { ngTemplate: this.nameCol },
      },
      {
        flex: 0,
        width: 58,
        headerName: '',
        field: 'action',
        resizable: true,
        cellRenderer: CustomRendererComponent,
        cellRendererParams: { ngTemplate: this.actionCol },
      },
    ];
  }

  createGridOptions(): GridOptions {
    return {
      ...this.env.defaultGridOptions,
    } as GridOptions;
  }

  setIconPtEntityAndTooltip() {
    switch (this.dependencyEntity) {
      case 'FolderEntity':
        this.iconPtEntity = 'folder';
        this.i18NTooltip = 'LABELS.FOLDER_WORKSPACE';
        break;
      case 'ApplicationEntity':
        this.iconPtEntity = 'applications';
        this.i18NTooltip = 'LABELS.APPLICATION';
        break;
      case 'AppointmentAuthorizedEntity':
        this.iconPtEntity = 'appointments';
        this.i18NTooltip = 'LABELS.APPOINTMENT';
        // ?at=auth
        break;
      case 'AppointmentResponsibleEntity':
        // at=resp
        this.iconPtEntity = 'appointments';
        this.i18NTooltip = 'LABELS.APPOINTMENT';
        break;
      case 'AppointmentSystemAdminEntity':
        // ?at=sys
        this.iconPtEntity = 'appointments';
        this.i18NTooltip = 'LABELS.APPOINTMENT';
        break;
      case 'ApplicationTypeEntity':
        this.iconPtEntity = 'applicationTypes';
        this.i18NTooltip = 'LABELS.APPLICATION_TYPE';
        break;
      case 'EmploymentEntity':
        this.iconPtEntity = 'employmentRelationship';
        this.i18NTooltip = 'LABELS.EMPLOYMENT';
        break;
      case 'LegalEntityEntity':
        this.iconPtEntity = 'legalEntity';
        this.i18NTooltip = 'LABELS.LEGAL_ENTITY';
        break;
      case 'NoticeEntity':
        this.iconPtEntity = 'notices';
        this.i18NTooltip = 'LABELS.NOTICE';
        break;
      case 'OrganizationalFunctionEntity':
        this.iconPtEntity = 'organizationalFunctions';
        this.i18NTooltip = 'LABELS.ORGANIZATIONAL_FUNCTION';
        break;
      case 'PrivacyRoleEntity':
        this.iconPtEntity = 'privacyRole';
        this.i18NTooltip = 'LABELS.PRIVACY_ROLE';
        break;
      case 'PurposeEntity':
        this.iconPtEntity = 'purposeCategory';
        this.i18NTooltip = 'LABELS.PURPOSE_CATEGORY';
        break;
      case 'RecipientEntity':
        this.iconPtEntity = 'recipients';
        this.i18NTooltip = 'LABELS.RECIPIENT';
        break;
      case 'RegulationEntity':
        this.iconPtEntity = 'regulations';
        this.i18NTooltip = 'LABELS.REGULATION';
        break;
      case 'SupplierEntity':
        this.iconPtEntity = 'suppliers';
        this.i18NTooltip = 'LABELS.SUPPLIER';
        break;
      case 'TreatmentCategoryEntity':
        this.iconPtEntity = 'treatmentCategory';
        this.i18NTooltip = 'LABELS.TREATMENT_CATEGORY';
        break;
      case 'UserResourceEntity':
        this.iconPtEntity = 'userResources';
        this.i18NTooltip = 'LABELS.USER_RESOURCE';
        break;
      case 'AppAssessmentDocEntity':
        this.iconPtEntity = 'appAssessment';
        this.i18NTooltip = 'LABELS.APP_ASSESSMENT';
        break;
      case 'GlobalMeasureCheckDocEntity':
        this.iconPtEntity = 'globalMeasureCheck';
        this.i18NTooltip = 'LABELS.RISK_ASSESSMENT_TOOLTIP';
        break;
      case 'AiAssessmentDocEntity':
        this.iconPtEntity = 'aiAssessment';
        this.i18NTooltip = 'LABELS.AI_ASSESSMENT';
        break;
      case 'CovidAssessmentDocEntity':
        this.iconPtEntity = 'covidAssessment';
        this.i18NTooltip = 'LABELS.COVID_ASSESSMENT';
        break;
      case 'TreatmentDocEntity':
        this.iconPtEntity = 'treatments';
        this.i18NTooltip = 'LABELS.TREATMENT';
        break;
      case 'DamageEntity':
        this.iconPtEntity = 'damage';
        this.i18NTooltip = 'LABELS.DAMAGE';
        break;
      case 'DataNatureEntity':
        this.iconPtEntity = 'natureOfData';
        this.i18NTooltip = 'LABELS.NATURE_OF_DATA';
        break;
      case 'InterestedCategoryEntity':
        this.iconPtEntity = 'interestedPersonCategories';
        this.i18NTooltip = 'LABELS.INTERESTED_CATEGORIES';
        break;
      case 'LegalBasisEntity':
        this.iconPtEntity = 'legalBasis';
        this.i18NTooltip = 'LABELS.LEGAL_BASIS';
        break;
      case 'OrganizationalMeasureEntity':
        this.iconPtEntity = 'organizationalMeasures';
        this.i18NTooltip = 'LABELS.ORGANIZATIONAL_MEASURES';
        break;
      case 'OrganizationalMeasureCategoryEntity':
        this.iconPtEntity = 'organizationalMeasureCategories';
        this.i18NTooltip = 'LABELS.ORGANIZATIONAL_MEASURES_CATEGORIES';
        break;
      case 'PreliminaryRiskAssessmentEntity':
        this.iconPtEntity = 'organizationalMeasureCategories';
        this.i18NTooltip = 'LABELS.PRELIMINARY_RISK_ASSESSMENT';
        break;
      case 'TechnicalMeasureEntity':
        this.iconPtEntity = 'technicalMeasures';
        this.i18NTooltip = 'LABELS.TECHNICAL_MEASURES';
        break;
      case 'TechnicalMeasureCategoryEntity':
        this.iconPtEntity = 'technicalMeasureCategories';
        this.i18NTooltip = 'LABELS.TECHNICAL_MEASURES_CATEGORIES';
        break;
    }
  }

  selectItem(row: ErrorDependenciesItem) {
    switch (this.dependencyEntity) {
      case 'FolderEntity':
        this.navigateToWorkSpace(row);
        break;
      case 'ApplicationEntity':
        this.navigateToDPG(PtUiAppRoutes.APPLICATIONS, row.id);
        break;
      case 'AppointmentAuthorizedEntity':
        this.navigateToBPM(PtUiAppRoutes.APPOINTMENTS, row.id, { at: 'auth' });
        // ?at=auth
        break;
      case 'AppointmentResponsibleEntity':
        // at=resp
        this.navigateToBPM(PtUiAppRoutes.APPOINTMENTS, row.id, { at: 'resp' });
        break;
      case 'AppointmentSystemAdminEntity':
        // ?at=sys
        this.navigateToBPM(PtUiAppRoutes.APPOINTMENTS, row.id, { at: 'sys' });
        break;
      case 'ApplicationTypeEntity':
        this.navigateToDPG(PtUiAppRoutes.APPLICATION_TYPES, row.id);
        break;
      case 'EmploymentEntity':
        this.navigateToDPG(PtUiAppRoutes.EMPLOYMENT_RELATIONSHIP, row.id);
        break;
      case 'LegalEntityEntity':
        this.navigateToDPG(PtUiAppRoutes.LEGAL_ENTITY, row.id);
        break;
      case 'NoticeEntity':
        this.navigateToDPG(PtUiAppRoutes.NOTICES, row.id);
        break;
      case 'OrganizationalFunctionEntity':
        this.navigateToDPG(PtUiAppRoutes.ORGANIZATIONAL_FUNCTIONS, row.id);
        break;
      case 'PrivacyRoleEntity':
        this.navigateToDPG(PtUiAppRoutes.PRIVACY_ROLES, row.id);
        break;
      case 'PurposeEntity':
        this.navigateToDPG(PtUiAppRoutes.PURPOSE_CATEGORIES, row.id);
        break;
      case 'RecipientEntity':
        this.navigateToDPG(PtUiAppRoutes.RECIPIENTS, row.id);
        break;
      case 'RegulationEntity':
        this.navigateToDPG(PtUiAppRoutes.REGULATIONS, row.id);
        break;
      case 'SupplierEntity':
        this.navigateToDPG(PtUiAppRoutes.SUPPLIERS, row.id);
        break;
      case 'TreatmentCategoryEntity':
        this.navigateToDPG(PtUiAppRoutes.TREATMENT_CATEGORY, row.id);
        break;
      case 'UserResourceEntity':
        this.navigateToDPG(PtUiAppRoutes.USERS_RESOURCES, row.id);
        break;
      case 'AppAssessmentDocEntity':
        this.navigateToBPM(PtUiAppRoutes.APP_ASSESSMENTS, row.id);
        break;
      case 'GlobalMeasureCheckDocEntity':
        this.navigateToBPM(PtUiAppRoutes.GLOBAL_MEASURE_CHECK, row.id);
        break;
      case 'AiAssessmentDocEntity':
        this.navigateToBPM(PtUiAppRoutes.AI_ASSESSMENTS, row.id);
        break;
      case 'CovidAssessmentDocEntity':
        this.navigateToBPM(PtUiAppRoutes.COVID_ASSESSMENTS, row.id);
        break;
      case 'TreatmentDocEntity':
        this.errorDependencyService.findById(row.path).subscribe((response) => {
          switch (response.treatmentType as TreatmentType) {
            case 'HOLDER':
              this.navigateToBPM(PtUiAppRoutes.TREATMENTS_HOLDER, row.id);
              break;
            case 'RESPONSIBLE':
              this.navigateToBPM(PtUiAppRoutes.TREATMENTS_RESPONSIBLE, row.id);
              break;
            case 'SUB_RESPONSIBLE':
              this.navigateToBPM(
                PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE,
                row.id
              );
              break;
            case 'PROJECT':
              this.navigateToBPM(PtUiAppRoutes.PROJECT_CARDS, row.id);
              break;
          }
        });
        break;
      case 'DamageEntity':
        this.navigateToADM(PtUiAppRoutes.DAMAGES, row.id);
        break;
      case 'DataNatureEntity':
        this.navigateToADM(PtUiAppRoutes.NATURE_OF_DATA, row.id);
        break;
      case 'InterestedCategoryEntity':
        this.navigateToADM(PtUiAppRoutes.INTERESTED_PERSON_CATEGORIES, row.id);
        break;
      case 'LegalBasisEntity':
        this.navigateToADM(PtUiAppRoutes.LEGAL_BASIS, row.id);
        break;
      case 'OrganizationalMeasureEntity':
        this.navigateToADM(PtUiAppRoutes.SECURITY_MEASURES, row.id);
        break;
      case 'OrganizationalMeasureCategoryEntity':
        this.navigateToADM(PtUiAppRoutes.SECURITY_MEASURE_CATEGORIES, row.id);
        break;
      case 'PreliminaryRiskAssessmentEntity':
        this.navigateToADM(PtUiAppRoutes.PRELIMINARY_RISK_ASSESSMENTS, row.id);
        break;
      case 'TechnicalMeasureEntity':
        this.navigateToADM(PtUiAppRoutes.TECHNICAL_MEASURES, row.id);
        break;
      case 'TechnicalMeasureCategoryEntity':
        this.navigateToADM(PtUiAppRoutes.TECHNICAL_MEASURE_CATEGORIES, row.id);
        break;
    }
  }

  navigateToDPG(entityPath: string, entityId: string) {
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE, entityPath],
        queryParams: {
          id: entityId,
          opened: true,
        },
        onBlankPage: true,
      })
    );
  }

  navigateToBPM(
    entityPath: string,
    entityId: string,
    queryParams?: Record<string, string>
  ) {
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [PtUiAppRoutes.BUSINESS_MANAGEMENT, entityPath],
        queryParams: {
          id: entityId,
          opened: true,
          ...queryParams,
        },
        onBlankPage: true,
      })
    );
  }

  navigateToADM(entityPath: string, entityId: string) {
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION, entityPath],
        queryParams: {
          id: entityId,
        },
        onBlankPage: true,
      })
    );
  }

  navigateToWorkSpace(item: ErrorDependenciesItem) {
    // todo workspace eseguire la ricerca al reload della pagina?
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [PtUiAppRoutes.WORKSPACE, 'resources', item.id],
        // queryParams: {search: item.name},
        onBlankPage: true,
      })
    );
  }

  private process(error: DeleteDependencies, tooltip: string) {
    if (error.path) {
      this.errorDependencyService
        .findById(error.path)
        .subscribe((response: DocTypes) => {
          this.currentEntity = response;
        });

      this.rowData = this.error?.dependencies?.items.map((m) => {
        return { ...m, tooltip: tooltip };
      });
    } else {
      this.rowData = this.error?.dependencies?.items.map((item) => {
        return {
          ...item,
          name: `${this.dependencyEntity}_${item.id}`,
          tooltip: tooltip,
        };
      });
      this.columnDefs = this.columnDefs.filter((c) => c.field !== 'action');
    }
  }
}


export class CustomTooltip implements ITooltipComp {
  eGui: any;

  init(params: ITooltipParams & { color: string }) {
    const eGui = (this.eGui = document.createElement('div'));
    const bgColor = 'white';
    const data = params.api!.getDisplayedRowAtIndex(params.rowIndex!)!.data;

    eGui.classList.add('custom-tooltip');
    //@ts-ignore
    eGui.style['background-color'] = bgColor;
    if (data.tooltip) {
      eGui.innerHTML = `
           <p>
               ${data.tooltip}
           </p>
      `;
    } else {
      eGui.innerHTML = '';
    }
  }

  getGui() {
    return this.eGui;
  }
}
